// branchUtils.js - Utilitaires partagés pour la gestion des branches familiales (paternelle/maternelle)

/**
 * Types de branches familiales
 */
export const BRANCH_TYPES = {
  ROOT: 'root',           // Personne racine
  PATERNAL: 'paternal',   // Branche paternelle
  MATERNAL: 'maternal',   // Branche maternelle
  DESCENDANT: 'descendant', // Descendants de la personne racine
  SPOUSE: 'spouse',       // Conjoints (sans branche spécifique)
  SPOUSE_FAMILY: 'spouse-family', // Famille du conjoint
  ALL: 'all'              // Toutes les branches (pour filtres)
};

/**
 * Couleurs des branches basées sur la palette GeneaFan
 * --color-dark: #32273b (violet/prune) -> branche paternelle
 * --color-light: #c8a47e (beige/doré) -> branche maternelle
 */
export const BRANCH_COLORS = {
  // Branche paternelle - tons violets/prune
  paternal: {
    male: '#32273b',      // --color-dark
    female: '#5a4a6b',    // Version plus claire du violet
    border: '#241b2a'     // Plus foncé pour bordure
  },
  // Branche maternelle - tons beige/dorés
  maternal: {
    male: '#8b6f47',      // Marron doré
    female: '#c8a47e',    // --color-light
    border: '#6d5639'     // Plus foncé pour bordure
  },
  // Personne racine - couleur neutre distinctive
  root: {
    male: '#df4d40',      // Rouge-orange (accent)
    female: '#df4d40',    // Même couleur pour distinction
    border: '#b33d32'     // Plus foncé pour bordure
  },
  // Descendants - tons neutres
  descendant: {
    male: '#5c7a6f',      // Vert-gris
    female: '#7fa396',    // Vert-gris clair
    border: '#4a6359'     // Plus foncé pour bordure
  },
  // Conjoints et leur famille - tons neutres chauds
  spouse: {
    male: '#a68a64',      // Beige moyen
    female: '#d4b896',    // Beige clair
    border: '#8a7252'     // Plus foncé pour bordure
  }
};

/**
 * Détermine la branche et la génération à partir d'un numéro Sosa
 * Le numéro Sosa encode la position dans l'arbre :
 * - 1 = ego (personne de référence)
 * - 2 = père, 3 = mère
 * - 4-5 = grands-parents paternels, 6-7 = grands-parents maternels
 *
 * En binaire, le premier bit après le '1' initial indique la branche :
 * - 0 = paternel (père = 10, grands-parents paternels = 100, 101)
 * - 1 = maternel (mère = 11, grands-parents maternels = 110, 111)
 *
 * @param {number} sosaNumber - Numéro Sosa de l'ancêtre
 * @returns {{ branch: string, generation: number }} Branche et génération
 */
export function getBranchFromSosa(sosaNumber) {
  if (!sosaNumber || sosaNumber < 1) {
    return { branch: BRANCH_TYPES.ROOT, generation: 0 };
  }

  if (sosaNumber === 1) {
    return { branch: BRANCH_TYPES.ROOT, generation: 0 };
  }

  const binaryRep = sosaNumber.toString(2);
  const generation = binaryRep.length - 1;
  const firstBit = binaryRep[1]; // Premier bit après le '1' initial

  return {
    branch: firstBit === '0' ? BRANCH_TYPES.PATERNAL : BRANCH_TYPES.MATERNAL,
    generation: generation
  };
}

/**
 * Branche ('root' | 'paternal' | 'maternal') d'un numéro Sosa, avec repli sur
 * 'root' pour la personne racine ou un Sosa absent/invalide (< 2).
 * Raccourci de getBranchFromSosa(sosa).branch, partagé par les contrôleurs de carte.
 *
 * @param {number} sosa - Numéro Sosa
 * @returns {string} Type de branche (BRANCH_TYPES)
 */
export function branchOf(sosa) {
  if (!sosa || sosa < 2) return BRANCH_TYPES.ROOT;
  return getBranchFromSosa(sosa).branch;
}

/**
 * Détermine la branche d'une personne par rapport à la personne racine
 * en parcourant les relations parent-enfant
 *
 * @param {string} rootId - ID de la personne racine
 * @param {string} personId - ID de la personne dont on veut la branche
 * @param {Map} membersMap - Map des membres (id -> FamilyMember)
 * @returns {string} Type de branche (BRANCH_TYPES)
 */
export function getBranchFromRelation(rootId, personId, membersMap) {
  if (personId === rootId) {
    return BRANCH_TYPES.ROOT;
  }

  const root = membersMap.get(rootId);
  if (!root) {
    return BRANCH_TYPES.SPOUSE; // Fallback
  }

  // Vérifier si c'est le père ou un ancêtre paternel
  if (root.fatherId === personId) {
    return BRANCH_TYPES.PATERNAL;
  }

  // Vérifier si c'est la mère ou un ancêtre maternel
  if (root.motherId === personId) {
    return BRANCH_TYPES.MATERNAL;
  }

  // Vérifier si c'est un conjoint
  if (root.spouseIds && root.spouseIds.includes(personId)) {
    return BRANCH_TYPES.SPOUSE;
  }

  // Vérifier si c'est un descendant
  if (root.childIds && root.childIds.includes(personId)) {
    return BRANCH_TYPES.DESCENDANT;
  }

  // Vérifier les ancêtres paternels (grands-parents paternels et au-delà)
  if (root.fatherId) {
    const fatherBranch = getBranchFromRelation(root.fatherId, personId, membersMap);
    if (fatherBranch === BRANCH_TYPES.PATERNAL || fatherBranch === BRANCH_TYPES.MATERNAL || fatherBranch === BRANCH_TYPES.ROOT) {
      return BRANCH_TYPES.PATERNAL; // Tout ancêtre du père est paternel
    }
  }

  // Vérifier les ancêtres maternels (grands-parents maternels et au-delà)
  if (root.motherId) {
    const motherBranch = getBranchFromRelation(root.motherId, personId, membersMap);
    if (motherBranch === BRANCH_TYPES.PATERNAL || motherBranch === BRANCH_TYPES.MATERNAL || motherBranch === BRANCH_TYPES.ROOT) {
      return BRANCH_TYPES.MATERNAL; // Tout ancêtre de la mère est maternel
    }
  }

  // Vérifier les descendants (petits-enfants, etc.)
  if (root.childIds) {
    for (const childId of root.childIds) {
      const child = membersMap.get(childId);
      if (child) {
        // Conjoint de l'enfant
        if (child.spouseIds && child.spouseIds.includes(personId)) {
          return BRANCH_TYPES.SPOUSE;
        }
        // Petits-enfants
        if (child.childIds && child.childIds.includes(personId)) {
          return BRANCH_TYPES.DESCENDANT;
        }
      }
    }
  }

  // Vérifier si c'est la famille d'un conjoint (beaux-parents)
  if (root.spouseIds) {
    for (const spouseId of root.spouseIds) {
      const spouse = membersMap.get(spouseId);
      if (spouse) {
        if (spouse.fatherId === personId || spouse.motherId === personId) {
          return BRANCH_TYPES.SPOUSE_FAMILY;
        }
      }
    }
  }

  // Fallback
  return BRANCH_TYPES.SPOUSE;
}

/**
 * Retourne la couleur appropriée pour une personne selon sa branche et son genre
 *
 * @param {string} branch - Type de branche (BRANCH_TYPES)
 * @param {string} gender - Genre ('male', 'female', ou autre)
 * @returns {{ fill: string, stroke: string }} Couleurs de remplissage et de bordure
 */
export function getBranchColor(branch, gender) {
  const branchColors = BRANCH_COLORS[branch] || BRANCH_COLORS.spouse;
  const genderKey = gender === 'male' || gender === 'M' ? 'male' : 'female';

  return {
    fill: branchColors[genderKey],
    stroke: branchColors.border
  };
}

/**
 * Calcule les branches pour tous les membres d'une famille étendue
 *
 * @param {string} rootId - ID de la personne racine
 * @param {Array} members - Tableau des membres de la famille
 * @param {Map} membersMap - Map des membres (id -> FamilyMember)
 * @returns {Map<string, string>} Map (personId -> branchType)
 */
export function calculateBranchesForFamily(rootId, members, membersMap) {
  const branches = new Map();

  for (const member of members) {
    const branch = getBranchFromRelation(rootId, member.id, membersMap);
    branches.set(member.id, branch);
  }

  return branches;
}
